import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormLabel from '@mui/material/FormLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const PLACEHOLDER_RE = /@[^@]+@/;

const getPlainText = (value = '') => value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  idSurvey,
  skipConfirmation,
  message,
  dialogPosition,
  confirmationInput,
  reminderDays,
  setReminderDays,
  switchChecked,
  setSwitchChecked,
  errorReminderDay,
  is360,
  canConfirm,
  title,
  showDispatchMessage,
  dispatchMessage,
  setDispatchMessage,
  dispatchEmailMask,
  setDispatchEmailMask,
  dispatchEmailSubject,
  setDispatchEmailSubject,
  quillModules,
}) => {

  const [confirming, setConfirming] = useState(false);
  const [timer, setTimer] = useState(skipConfirmation ? 5 : 0); // Temporizador de 5 segundos
  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para controlar si se está procesando
  useEffect(() => {
    let timeout;
    if (confirming && timer > 0) {
      timeout = setTimeout(() => setTimer(timer - 1), 1000); // Decrementa el temporizador cada segundo
    } else if (confirming && timer === 0) {
      handleConfirm();
    }

    return () => clearTimeout(timeout);
  }, [confirming, timer]);

  const handleStartConfirming = () => {
    setConfirming(true);
    setIsSubmitting(true);
  };

  const messageError =
    showDispatchMessage && !getPlainText(dispatchMessage)
      ? 'El mensaje es requerido.'
      : showDispatchMessage && !PLACEHOLDER_RE.test(dispatchMessage || '')
        ? 'El mensaje debe contener un marcador tipo "@algo@".'
        : '';
  const emailMaskError =
    showDispatchMessage && !dispatchEmailMask?.trim()
      ? 'La máscara del correo es requerida.'
      : showDispatchMessage && dispatchEmailMask.trim().length < 5
        ? 'Mínimo 5 caracteres.'
        : showDispatchMessage && dispatchEmailMask.trim().length > 30
          ? 'Máximo 30 caracteres.'
          : '';
  const emailSubjectError =
    showDispatchMessage && !dispatchEmailSubject?.trim()
      ? 'El asunto es requerido.'
      : showDispatchMessage && dispatchEmailSubject.trim().length < 5
        ? 'Mínimo 5 caracteres.'
        : '';

  const handleConfirm = async () => {
    try {
      await onConfirm(idSurvey, dialogPosition, {
        message: dispatchMessage,
        emailMask: dispatchEmailMask,
        emailSubject: dispatchEmailSubject,
      });
    } finally {
      setConfirming(false);
      setTimer(skipConfirmation ? 5 : 0); // Restablece el temporizador a 5 segundos
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setConfirming(false);
    setTimer(skipConfirmation ? 5 : 0); // Restablece el temporizador a 5 segundos
    setIsSubmitting(false);
    onClose();
  };

  const handleReminderDay = (event) => {
    const value = event.target.value;
    setReminderDays(value);
  };

  const handleSwitchChange = (event) => {
    setSwitchChecked(event.target.checked);
  };

  useEffect(() => {
    if (errorReminderDay === false) {
      onClose();
    }
  }, [errorReminderDay]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{title || 'Confirmar Acción'}</DialogTitle>
      <DialogContent>
       {!is360 && (
          typeof message === 'string'
            ? <DialogContentText>{message}</DialogContentText>
            : <div>{message}</div>
        )}
        <div style={{ marginTop: '15px' }}>
          {confirmationInput && !is360 && (
            <>
              <TextField
                error={errorReminderDay}
                id="outlined-error-helper-text"
                label="Ingrese los días"
                value={reminderDays}
                helperText="Este campo no puede estar vacío, ni puede contener el valor de 0"
                onChange={handleReminderDay}
                disabled={switchChecked} // Desactiva el TextField si el Switch está desactivado
              />

              <div style={{ marginTop: '15px' }}>
                <Switch
                  checked={switchChecked}
                  onChange={handleSwitchChange} // Maneja el cambio de estado
                />
                <span>
                  {
                    'Activa esta opción para enviar recordatorios sin importar la cantidad de días.'
                  }
                </span>
              </div>
            </>
          )}
        </div>

        {showDispatchMessage && (
          <div style={{ marginTop: '15px' }}>
            <TextField
              label="Máscara del correo"
              fullWidth
              variant="outlined"
              size="small"
              value={dispatchEmailMask || ''}
              error={!!emailMaskError}
              helperText={emailMaskError || 'Nombre que verá el destinatario como remitente.'}
              onChange={(event) => setDispatchEmailMask(event.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Asunto del correo"
              fullWidth
              variant="outlined"
              size="small"
              value={dispatchEmailSubject || ''}
              error={!!emailSubjectError}
              helperText={emailSubjectError || 'Asunto que recibirán los destinatarios.'}
              onChange={(event) => setDispatchEmailSubject(event.target.value)}
              sx={{ mb: 2 }}
            />
            <FormLabel
              sx={{
                mb: 0.5,
                fontSize: '0.875rem',
                color: messageError ? '#d32f2f' : 'rgba(0,0,0,0.6)',
                display: 'block',
              }}
            >
              Mensaje:
            </FormLabel>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              value={dispatchMessage || ''}
              onChange={setDispatchMessage}
              placeholder="Escribe el mensaje para los usuarios..."
              style={{ marginBottom: '4px' }}
            />
            <Typography
              variant="caption"
              sx={{ color: messageError ? '#d32f2f' : 'rgba(0,0,0,0.6)' }}
            >
              {messageError ||
                'Cada usuario recibirá este mensaje. @enlace@ será reemplazado por el link de la encuesta.'}
            </Typography>
          </div>
        )}

        <DialogContentText>
          ¿Estás seguro de que deseas ejecutar esta acción?
        </DialogContentText>

        {confirming && skipConfirmation && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '20px',
              flexDirection: 'column',
            }}
          >
            <CircularProgress />
            <p>{`Confirmando en ${timer} segundos...`}</p>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {confirming && skipConfirmation ? (
          <Button onClick={handleCancel} color="primary">
            Cancelar confirmación
          </Button>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              color="primary"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleStartConfirming}
              color="primary"
              autoFocus
              disabled={
                isSubmitting ||
                (canConfirm !== undefined && !canConfirm) ||
                !!messageError ||
                !!emailMaskError ||
                !!emailSubjectError
              }
            >
              Confirmar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
